import React from 'react';
import { Avatar, Tag, Button, Divider, Drawer, Space, DrawerProps } from 'antd';
import { 
  CalendarOutlined, 
  ClockCircleOutlined, 
  RollbackOutlined, 
  CloseOutlined 
} from '@ant-design/icons';

// --- Interfaces from your context ---
interface MasterConfigs {
  isSteps?: boolean;
}

interface Props {
  children?: React.ReactNode;
  submitForm: () => void;
  forceClose?: () => void;
  loading: boolean;
  handleCancel: () => void;
  maskClosable?: boolean;
  visible: boolean;
  isView: boolean;
  title?: string | React.ReactNode;
  rest?: DrawerProps;
  config?: MasterConfigs;
  stepsIndex?: { current: number; lastIndex: number };
}

// --- Feedback Data Types ---
interface FeedbackItem {
  name: string;
  role: string;
  date: string;
  time: string;
  status: 'Selected' | 'Rejected';
  interviewType: string;
  clientName: string;
  company: string;
  feedbackText: string;
  rejectionReason?: string;
  timestamp: string;
}

const mockFeedback: FeedbackItem[] = [
  {
    name: 'Abhi Goyal',
    role: 'Angular Developer',
    date: '15 Sep, 2025',
    time: '12:00 PM - 01:00 PM',
    status: 'Selected',
    interviewType: 'L1 Interview',
    clientName: 'John Smith',
    company: 'ABC Company',
    feedbackText: 'The interview process was very smooth and well-structured. I appreciated the timely communication and clear expectations at each stage.',
    timestamp: '15 Sep, 2025 at 01:30 PM',
  },
  {
    name: 'Abhi Goyal',
    role: 'Angular Developer',
    date: '18 Sep, 2025',
    time: '12:00 PM - 01:00 PM',
    status: 'Rejected',
    interviewType: 'L1 Interview',
    clientName: 'John Smith',
    company: 'ABC Company',
    rejectionReason: 'Lorem ipsum dolor sit amet consectetur.',
    feedbackText: 'The interview process was very smooth and well-structured. I appreciated the timely communication and clear expectations at each stage.',
    timestamp: '18 Sep, 2025 at 01:30 PM',
  }
];

// --- Sub-component: The Feedback Card ---
const FeedbackCard: React.FC<{ item: FeedbackItem }> = ({ item }) => (
  <div className="bg-white border border-gray-100 rounded-lg p-5 mb-4 shadow-sm font-sans">
    <div className="flex justify-between items-start mb-4">
      <div className="flex items-center gap-3">
        <Avatar size={44} src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${item.name}`} />
        <div>
          <h4 className="text-[14px] font-bold text-gray-800 m-0 leading-tight">{item.name}</h4>
          <span className="text-[11px] text-gray-500">{item.role}</span>
        </div>
      </div>
      <Tag color="blue" className="rounded-md border-none bg-blue-50 text-blue-600 font-medium px-3 py-0.5 text-[11px]">
        {item.interviewType}
      </Tag>
    </div>

    <div className="mb-4">
      <span className="text-[10px] uppercase text-gray-400 font-bold tracking-tight">Interview Detail</span>
      <div className="flex items-center gap-4 mt-1">
        <div className="flex items-center gap-1.5 text-gray-600 text-[13px]">
          <CalendarOutlined className="text-gray-400" />
          <span>{item.date}</span>
        </div>
        <div className="flex items-center gap-1.5 text-gray-600 text-[13px]">
          <ClockCircleOutlined className="text-gray-400" />
          <span>{item.time}</span>
        </div>
        <Tag color={item.status === 'Selected' ? 'success' : 'error'} className="ml-1 rounded px-2 py-0 text-[11px] font-semibold">
          {item.status}
        </Tag>
      </div>
    </div>

    {!item.rejectionReason ? null : (
      <div className="mb-4">
        <span className="text-[10px] uppercase text-gray-400 font-bold tracking-tight">Rejection reason</span>
        <p className="text-[13px] text-gray-700 mt-0.5">{item.rejectionReason}</p>
      </div>
    )}

    <div className="pt-3 border-t border-gray-50">
      <div className="flex justify-between items-center mb-2">
        <span className="text-[10px] uppercase text-gray-400 font-bold tracking-tight">Client Interviewer</span>
        <span className="text-[10px] text-gray-400">{item.timestamp}</span>
      </div>
      <div className="flex items-center gap-2 mb-2">
        <Avatar size={24} className="bg-gray-100 text-gray-600 font-bold text-[10px]">JS</Avatar>
        <div className="flex flex-col">
          <span className="text-[12px] font-bold text-gray-800">{item.clientName}</span>
          <span className="text-[10px] text-gray-400">{item.company}</span>
        </div>
      </div>
      <p className="text-[13px] text-gray-600 italic leading-relaxed">"{item.feedbackText}"</p>
      <div className="flex justify-end mt-1">
        <Button type="text" size="small" icon={<RollbackOutlined />} className="text-gray-400 hover:text-blue-500 text-[11px] bg-gray-50 px-3">
          Reply
        </Button>
      </div>
    </div>
  </div>
);

// --- Main Drawer Component ---
const FeedbackDrawer: React.FC<Props> = ({ 
  submitForm, 
  loading, 
  handleCancel, 
  maskClosable = false, 
  visible, 
  isView, 
  title = "Recent Feedback", 
  config, 
  forceClose, 
  stepsIndex,
  ...rest 
}) => {

  return (
    <Drawer
      maskClosable={maskClosable}
      width={1000} // Adjusted to match the screenshot aspect ratio better
      title={
        <div className="flex justify-between items-center w-full pr-4">
          <span className="text-lg font-bold">{title}</span>
          <a className="text-blue-500 text-sm font-semibold hover:underline">See All</a>
        </div>
      }
      placement="right"
      onClose={() => (forceClose ? forceClose() : handleCancel())}
      open={visible}
      destroyOnClose={true}
      closeIcon={null} // We are handling custom header feel
      extra={<CloseOutlined onClick={handleCancel} className="text-gray-400 cursor-pointer" />}
      footer={
        isView ? null : (
          <Space direction="horizontal">
            <Button key="back" onClick={handleCancel} className="mr-2">
              {config?.isSteps && (stepsIndex?.current ?? 0) > 0 ? 'Previous' : 'Cancel'}
            </Button>
            <Button
              key="submit"
              type="primary"
              loading={loading}
              onClick={submitForm}
            >
              {config?.isSteps && stepsIndex?.current !== stepsIndex?.lastIndex ? 'Save & Next' : 'Save'}
            </Button>
          </Space>
        )
      }
      footerStyle={{ textAlign: 'end' }}
      bodyStyle={{ backgroundColor: '#F9FAFB', padding: '24px' }}
      {...rest?.rest}
    >
      <div className="flex flex-col">
        {mockFeedback.map((item, idx) => (
          <FeedbackCard key={idx} item={item} />
        ))}
      </div>
    </Drawer>
  );
};

export default FeedbackDrawer;